import { Card, ChipMetadata, LinkButton, Tooltip } from '@pmi/dsm-react';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import { ReactNode, useCallback, useMemo } from 'react';
import { DotSolidIcon } from '@pmi/dsm-react/dist/components/icons';
import styles from './userCardListTemplate.module.scss';
import { PeopleCardListItemProperty } from '../../PeopleCardList';
import { UserActions } from '../UsersList/models';
import {
  User,
  UserDeal,
  UserGroup,
} from '../../../../../shared/models/UserModels';
import AssignedProductCell from '../UsersList/TableCells/AssignedProductCell';
import {
  getTextForNoPersonId,
  toDayMonthYear,
} from '../../../../../shared/helpers';
import { toastWithAnalytics } from '../../../../../shared/analytics';
import { useResendUserInvitationMutation } from '../UsersList/apiSlice';
import { addUsersResend, usersResendList } from '../../resendActionSlice';
import { RowSelectionCell } from '../UsersList/TableCells/RowSelectionHeader';

interface UserCardListTemplateProps<T, A>
  extends PeopleCardListItemProperty<T, A> {
  isInternalAdmin: boolean;
}

export default function UserCardListTemplate({
  item,
  actions,
  isInternalAdmin,
}: UserCardListTemplateProps<User, UserActions>) {
  const dispatch = useDispatch();
  const {
    id,
    personName,
    email,
    createdByPersonFullName: createdByPersonName,
    createdDate: created,
    personId,
    isRemoved,
    removedDate: removed,
    countryCode,
    hasAcceptedToShareData: shareData,
    invitationStatus = 'Pending',
    hasFailedEmail = false,
  } = item;
  const [resendInvitation, resendInvitationRequest] =
    useResendUserInvitationMutation();
  const isInResendList = useSelector(usersResendList).includes(email);
  const accessDomain = useMemo(() => {
    const domainStartIndex = email.indexOf('@');
    return domainStartIndex > -1
      ? email.substring(domainStartIndex + 1)
      : email;
  }, [email]);

  const onResendClick = useCallback(
    async (emailAdd: string, accountUserId: string = '') => {
      try {
        await resendInvitation({ accountUserId }).unwrap();
        const message = (): ReactNode => (
          <>
            Invite resent to <span data-exclude-lg="true">{emailAdd}</span>
          </>
        );
        toastWithAnalytics(message(), 'success');
        resendInvitationRequest.reset();
      } catch {
        toastWithAnalytics(
          'Could not resend invitation. Please try again.',
          'danger',
        );
      }
    },
    [resendInvitation, resendInvitationRequest],
  );

  const handleResendClick = useCallback(() => {
    if (isInResendList) return;
    dispatch(addUsersResend(email));
    onResendClick(email, id);
  }, [dispatch, email, id, isInResendList, onResendClick]);
  const renderRegistered = () => (
    <span className="d-block">
      {invitationStatus !== 'Pending' ? (
        <span>{toDayMonthYear(created)}</span>
      ) : (
        <span className={styles.pendingInvite}>
          <span className="d-block me-1">
            {hasFailedEmail ? 'Undeliverable' : 'Pending'}
          </span>
          {resendInvitationRequest.isLoading ? (
            <Spinner size="sm" className="text-primary ms-1" />
          ) : (
            <LinkButton
              size="xs"
              className={styles.resendInvite}
              titleText="Resend"
              disabled={isInResendList}
              onClick={handleResendClick}
            />
          )}
        </span>
      )}
    </span>
  );
  const renderAccess = (deals: UserDeal[]) => {
    if (isRemoved) {
      return (
        <span className="d-block">
          User Disabled{' '}
          {personId > 0 && removed && <span>{toDayMonthYear(removed)}</span>}
        </span>
      );
    }
    if (deals.length > 0) {
      const deal = deals[0];
      if (deal.groups && deal.groups.length > 0) {
        const group: UserGroup = deal.groups[0];
        if (group.domainAccess && !group.inviteAccess) {
          return (
            <>
              <span className="d-block fw-bold me-2">Access Method </span>
              <span className={`d-block ${styles.labelText}`} data-exclude-lg>
                Domain ({accessDomain})
              </span>
            </>
          );
        }
        if (group.inviteAccess) {
          return (
            <>
              <span className={`d-block ${styles.labelText}`}>
                Access Method
              </span>
              <span className="d-block">Email Invite</span>
            </>
          );
        }
      }
    }
    return null;
  };
  return (
    <Card className="listItemCard mb-3">
      <div className="p-0">
        <div className="p-4 pb-3">
          <div className="d-block mb-2">
            <div className="d-flex">
              {personName && (
                <span
                  className={`${styles.textContent} flex-grow-1 d-block`}
                  data-exclude-lg
                >
                  <LinkButton
                    className={styles.linkButton}
                    size="sm"
                    titleText={personName}
                    onClick={() => actions.onView(item)}
                  />
                </span>
              )}
              <span className={`${styles.checkboxSize} d-block mb-1`}>
                <RowSelectionCell row={{ original: item }} />
              </span>
            </div>
            {email && (
              <span className={styles.commonText} data-exclude-lg>
                {email}
              </span>
            )}
            {isInternalAdmin && (
              <span
                className={`${styles.commonText} small d-block`}
                data-exclude-lg
              >
                <span>PMI ID:</span>
                <span>{personId || getTextForNoPersonId(item)}</span>
              </span>
            )}
            {isInternalAdmin && shareData === false && (
              <Tooltip
                type="sm"
                content="DO NOT SHARE product data including exam result, exam date, status, progress and completion with people outside PMI."
                placement="top"
                className="darkTooltip"
              >
                <span
                  className={`${styles.commonText} small d-block`}
                  data-exclude-lg
                >
                  <span className="small">
                    <ChipMetadata
                      text="Opted Out"
                      icon={<DotSolidIcon />}
                      variant="red"
                      className={styles.chipDanger}
                    />
                  </span>
                </span>
              </Tooltip>
            )}
            {invitationStatus === 'Pending' && (
              <div className={`d-flex flex-column ${styles.resendBlock}`}>
                {renderRegistered()}
              </div>
            )}
          </div>
          <div className={`d-block ${styles.commonText} mb-1`}>
            <span className={`d-block ${styles.labelText}`}>
              Assigned Products
            </span>
            <AssignedProductCell
              row={{ original: item }}
              productsNames={item.assignedProducts}
            />
          </div>
          <div className={`d-block ${styles.commonText} mb-1`}>
            {isInternalAdmin && (
              <div className="d-flex flex-column">
                <span className={`d-block ${styles.labelText}`}>
                  Country / Region
                </span>
                <span className="d-block">
                  {countryCode?.toString().length > 0 ? countryCode : 'N/A'}
                </span>
              </div>
            )}
          </div>
          <div className={`d-block ${styles.commonText} mb-1`}>
            {isInternalAdmin && shareData === false ? (
              <div className="d-flex flex-column">
                <span className={`d-block ${styles.labelText}`}>
                  Registration
                </span>
                <span className="d-block">Opted-out</span>
              </div>
            ) : (
              <>
                {personId > 0 && !isRemoved && (
                  <div className="d-flex flex-column mb-1">
                    <span className={`d-block ${styles.labelText}`}>
                      Registration
                    </span>
                    <span className="d-block">{renderRegistered()}</span>
                  </div>
                )}
                <div className="d-flex flex-column mb-1">
                  {renderAccess(item.deals)}
                </div>
              </>
            )}
          </div>
          <div className={`d-block ${styles.commonText} mb-1`}>
            <div className="d-flex flex-column">
              <span className="me-2" data-exclude-lg>
                <span className={`d-block ${styles.labelText}`}>
                  Invited By
                </span>
                <span className="d-block">{createdByPersonName}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
}
